import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Check, KeyRound, LogOut, Mail, Megaphone, ShieldCheck, Trash2, User, Wrench } from "lucide-react";
import { SiGmail, SiMicrosoftoffice, SiMicrosoftoutlook } from "react-icons/si";
import { IconType } from "react-icons";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { getMyInfo, updateMyInfo, deleteAccount, updatePassword } from "interfaces/user";
import { getMyApiKeys } from "interfaces/apiKeys";
import ApiKeysCard from "./ApiKeysCard";
import { DEFAULT_OFFER, OFFER_TOKEN } from "views/linkedin/vocabulary";
import { getShowBoMenu, isAdminUser, setShowBoMenu } from "utils/admin";

/** Same cap as the backend: the offer sits inside a 300-character invitation note. */
const OFFER_MAX = 200;

// Tabs are kept in the URL (?tab=) so each one can be linked to and the back button walks between them.
type Tab = "profile" | "prospecting" | "security" | "api" | "admin";

const TAB_META: Record<Tab, { label: string; icon: typeof User }> = {
    profile: { label: "Profil", icon: User },
    prospecting: { label: "Prospection", icon: Megaphone },
    security: { label: "Sécurité", icon: ShieldCheck },
    api: { label: "Accès API", icon: KeyRound },
    admin: { label: "Admin", icon: Wrench },
};

// Brand logos in their own colours: they are how people recognise their mailbox at a glance.
const EMAIL_PROVIDERS: { value: string; label: string; hint: string; icon: IconType | typeof Mail; color?: string }[] = [
    { value: "gmail", label: "Gmail", hint: "mail.google.com", icon: SiGmail, color: "#EA4335" },
    { value: "outlook-live", label: "Outlook", hint: "Live / Hotmail", icon: SiMicrosoftoutlook, color: "#0078D4" },
    { value: "outlook", label: "Outlook 365", hint: "outlook.office.com", icon: SiMicrosoftoutlook, color: "#0078D4" },
    { value: "office", label: "Office 365", hint: "outlook.office.com", icon: SiMicrosoftoffice, color: "#D83B01" },
    { value: "mailto", label: "Application par défaut", hint: "Lien mailto", icon: Mail },
];

function Modal({ open, title, children, onClose }: any) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
            <Card className="w-full max-w-md p-6">
                <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-ink">{title}</h4>
                    <button onClick={onClose} className="text-sm text-ink-muted transition-colors duration-150 hover:text-ink">Fermer</button>
                </div>
                <div>{children}</div>
            </Card>
        </div>
    );
}

/** A settings block: title and explanation on the left, the controls on the right (stacked on mobile). */
function Section({ title, description, children, tone }: { title: string; description?: ReactNode; children: ReactNode; tone?: "danger" }) {
    return (
        <div className="grid grid-cols-1 gap-4 py-6 first:pt-0 last:pb-0 md:grid-cols-3 md:gap-8">
            <div>
                <h2 className={`text-base font-semibold ${tone === "danger" ? "text-danger" : "text-ink"}`}>{title}</h2>
                {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
            </div>
            <div className="md:col-span-2">{children}</div>
        </div>
    );
}

function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
    return (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink">
            {children}
        </label>
    );
}

export default function SettingsView() {
    const [company, setCompany] = useState("");
    const [savedCompany, setSavedCompany] = useState("");
    const [email, setEmail] = useState("");
    const [emailProvider, setEmailProvider] = useState("gmail");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [password, setPassword] = useState("");
    const [oldPassword, setOldPassword] = useState("");
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [isGmail, setIsGmail] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hasApiKeys, setHasApiKeys] = useState(false);
    const [showAdminRed, setShowAdminRed] = useState(false);
    const [offer, setOffer] = useState("");
    const [savedOffer, setSavedOffer] = useState("");
    const [offerSaving, setOfferSaving] = useState(false);
    const offerRef = useRef<HTMLTextAreaElement>(null);
    const nav = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // API and Admin only exist for the accounts they concern: an empty tab reads as a broken page.
    const tabs: Tab[] = ["profile", "prospecting", "security", ...(hasApiKeys ? ["api" as Tab] : []), ...(isAdmin ? ["admin" as Tab] : [])];
    const tabParam = searchParams.get("tab") as Tab | null;
    // The LinkedIn invite dialog links to "/settings#offre"
    const requested: Tab | null = location.hash === "#offre" ? "prospecting" : tabParam;
    const activeTab: Tab = requested && tabs.includes(requested) ? requested : "profile";
    const setActiveTab = (tab: Tab) => {
        const next = new URLSearchParams(searchParams);
        if (tab === "profile") next.delete("tab"); else next.set("tab", tab);
        // Drop the #offre hash, or it would keep forcing the Prospection tab
        nav({ pathname: location.pathname, search: next.toString() ? `?${next}` : "" });
    };

    useEffect(() => {
        const load = async () => {
            const res: any = await getMyInfo();
            if (res?.data) {
                setCompany(res.data.company || "");
                setSavedCompany(res.data.company || "");
                setEmail(res.data.email || "");
                setEmailProvider(res.data.emailProvider || "gmail");
                setOffer(res.data.prospectingOffer || "");
                setSavedOffer(res.data.prospectingOffer || "");
                const mail = (res.data.email || "").toLowerCase();
                setIsGmail(mail.endsWith("@gmail.com") || mail.endsWith("@googlemail.com"));
            }
            setIsAdmin(isAdminUser(res?.data));
            setShowAdminRed(getShowBoMenu());
            setLoading(false);
        };
        load();
        getMyApiKeys()
            .then((r) => setHasApiKeys(r.keys.some((k) => !k.revokedAt)))
            .catch(() => setHasApiKeys(false));
    }, []);

    // Land on the offer field, ready to type.
    useEffect(() => {
        if (location.hash !== "#offre" || loading) return;
        const field = offerRef.current;
        field?.scrollIntoView({ behavior: "smooth", block: "center" });
        field?.focus();
    }, [location.hash, loading]);

    const profileDirty = company !== savedCompany;
    const offerDirty = offer.trim() !== savedOffer.trim();

    // A single click is the whole choice, so it is saved at once (and undone if the save fails).
    const handleProvider = async (value: string) => {
        if (value === emailProvider) return;
        const previous = emailProvider;
        setEmailProvider(value);
        const res: any = await updateMyInfo({ emailProvider: value });
        if (res?.data) {
            toast.success("Messagerie enregistrée");
        } else {
            setEmailProvider(previous);
            toast.error("Impossible d'enregistrer la messagerie.");
        }
    };

    const handleSave = async () => {
        setSaving(true);
        const res: any = await updateMyInfo({ company });
        if (res?.data) {
            setSavedCompany(company);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } else {
            toast.error("Impossible d'enregistrer le profil.");
        }
        setSaving(false);
    };

    const handleSaveOffer = async () => {
        setOfferSaving(true);
        const res: any = await updateMyInfo({ prospectingOffer: offer.trim() });
        if (res?.data) {
            setSavedOffer(offer.trim());
            toast.success("Offre enregistrée");
        } else {
            toast.error("Impossible d'enregistrer l'offre.");
        }
        setOfferSaving(false);
    };

    const handlePassword = async () => {
        if (isGmail || !oldPassword || !password) return;
        setPasswordSaving(true);
        const res = await updatePassword({ oldPassword, newPassword: password });
        setPasswordSaving(false);
        if (res.error) {
            toast.error("Mot de passe actuel incorrect ou nouveau mot de passe refusé.");
            return;
        }
        setOldPassword("");
        setPassword("");
        toast.success("Mot de passe mis à jour");
    };

    const handleDelete = async () => {
        setDeleting(true);
        const res: any = await deleteAccount();
        setDeleting(false);
        if (res?.data) {
            toast.success("Compte supprimé");
            localStorage.removeItem("token");
            localStorage.removeItem("user-token");
            nav("/auth/login");
        } else {
            toast.error("Impossible de supprimer le compte. Contactez le support.");
        }
    };

    const signOut = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user-token");
        toast.success("Déconnecté");
        nav("/auth/login");
    };

    const handleAdminRedToggle = () => {
        const newValue = !showAdminRed;
        setShowAdminRed(newValue);
        setShowBoMenu(newValue);
        toast.success(newValue ? "Menu Back-office activé" : "Menu Back-office masqué");
    };

    return (
        <div className="mx-auto max-w-4xl bg-canvas p-4 font-sans sm:p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-ink">Paramètres</h1>
                <p className="mt-1 text-sm text-ink-muted">Gérez votre profil, votre prospection et la sécurité de votre compte.</p>
            </div>

            <div role="tablist" className="mb-6 flex gap-1 overflow-x-auto border-b border-border">
                {tabs.map((tab) => {
                    const { label, icon: Icon } = TAB_META[tab];
                    const isActive = activeTab === tab;
                    return (
                        <button
                            key={tab}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveTab(tab)}
                            className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                isActive ? "border-accent text-ink" : "border-transparent text-ink-muted hover:text-ink"
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <Card>
                    <div className="space-y-3">
                        <div className="h-4 w-1/3 animate-pulse rounded bg-surface-sunken" />
                        <div className="h-11 w-full animate-pulse rounded-lg bg-surface-sunken" />
                        <div className="h-11 w-full animate-pulse rounded-lg bg-surface-sunken" />
                    </div>
                </Card>
            ) : (
                <>
                    {activeTab === "profile" && (
                        <Card className="divide-y divide-border">
                            <Section title="Informations" description="Votre entreprise apparaît dans vos messages. L'e-mail sert d'identifiant et ne peut pas être modifié.">
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="company">Entreprise</Label>
                                        <Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nom de votre entreprise" />
                                    </div>
                                    <div>
                                        <Label htmlFor="email">E-mail</Label>
                                        <Input id="email" value={email} disabled />
                                    </div>
                                </div>
                            </Section>
                            <div className="flex items-center justify-end gap-3 pt-6">
                                {saved && (
                                    <span className="flex items-center gap-1.5 text-sm text-success">
                                        <Check className="h-4 w-4" />
                                        Enregistré
                                    </span>
                                )}
                                <Button onClick={handleSave} disabled={saving || !profileDirty}>
                                    {saving ? "Enregistrement…" : "Enregistrer"}
                                </Button>
                            </div>
                        </Card>
                    )}

                    {activeTab === "prospecting" && (
                        <Card className="divide-y divide-border">
                            <Section
                                title="Votre offre"
                                description={
                                    <>
                                        Une phrase sur ce que vous proposez. Elle remplace la variable{" "}
                                        <span className="rounded-full bg-accent-subtle px-1.5 py-0.5 font-mono text-xs text-accent">{OFFER_TOKEN}</span> dans vos invitations
                                        LinkedIn.
                                    </>
                                }
                            >
                                <textarea
                                    id="offre"
                                    ref={offerRef}
                                    value={offer}
                                    onChange={(e) => setOffer(e.target.value.slice(0, OFFER_MAX))}
                                    rows={4}
                                    placeholder={DEFAULT_OFFER}
                                    className="w-full resize-none rounded-lg border border-border bg-surface p-3 text-sm text-ink placeholder:text-ink-faint focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                />
                                <div className="mt-1 flex items-start justify-between gap-3">
                                    <p className="text-xs text-ink-muted">
                                        {offer.trim()
                                            ? "Ex. : « Je livre des produits frais en 24 h aux restaurants lyonnais, sans minimum de commande. »"
                                            : "Sans offre définie, vos messages utilisent la phrase par défaut affichée ci-dessus."}
                                    </p>
                                    <span className="shrink-0 font-mono text-xs text-ink-faint">
                                        {offer.length} / {OFFER_MAX}
                                    </span>
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <Button onClick={handleSaveOffer} disabled={offerSaving || !offerDirty}>
                                        {offerSaving ? "Enregistrement…" : "Enregistrer mon offre"}
                                    </Button>
                                </div>
                            </Section>
                            <Section title="Messagerie" description="Le service ouvert quand vous rédigez un e-mail à un prospect depuis l'application.">
                                <div role="radiogroup" aria-label="Fournisseur d'e-mail" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {EMAIL_PROVIDERS.map(({ value, label, hint, icon: Icon, color }) => {
                                        const selected = emailProvider === value;
                                        return (
                                            <button
                                                key={value}
                                                role="radio"
                                                aria-checked={selected}
                                                onClick={() => handleProvider(value)}
                                                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                                    selected ? "border-ink bg-surface" : "border-border bg-surface hover:bg-surface-sunken"
                                                }`}
                                            >
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface">
                                                    <Icon className={`h-5 w-5 ${color ? "" : "text-ink-muted"}`} style={color ? { color } : undefined} />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium text-ink">{label}</span>
                                                    <span className="block truncate text-xs text-ink-muted">{hint}</span>
                                                </span>
                                                {selected && <Check className="h-4 w-4 shrink-0 text-ink" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Section>
                        </Card>
                    )}

                    {activeTab === "security" && (
                        <Card className="divide-y divide-border">
                            <Section
                                title="Mot de passe"
                                description={isGmail ? "Votre compte est une adresse Gmail : le mot de passe est géré par Google." : "Choisissez un mot de passe que vous n'utilisez nulle part ailleurs."}
                            >
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="oldPassword">Mot de passe actuel</Label>
                                        <Input
                                            id="oldPassword"
                                            type="password"
                                            autoComplete="current-password"
                                            value={oldPassword}
                                            onChange={(e) => setOldPassword(e.target.value)}
                                            disabled={isGmail}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                                        <Input
                                            id="newPassword"
                                            type="password"
                                            autoComplete="new-password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={isGmail}
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <Button onClick={handlePassword} disabled={isGmail || passwordSaving || !oldPassword || !password}>
                                            {passwordSaving ? "Mise à jour…" : "Mettre à jour le mot de passe"}
                                        </Button>
                                    </div>
                                </div>
                            </Section>
                            <Section title="Session" description="Déconnectez-vous de cet appareil. Vous pourrez vous reconnecter à tout moment.">
                                <Button variant="secondary" onClick={signOut}>
                                    <LogOut className="h-4 w-4" />
                                    Se déconnecter
                                </Button>
                            </Section>
                            <Section title="Supprimer le compte" tone="danger" description="Toutes vos données seront supprimées. Cette action est irréversible.">
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="inline-flex h-11 items-center gap-2 rounded-lg border border-danger/30 bg-surface px-5 text-sm font-medium text-danger transition-colors duration-150 hover:bg-danger-subtle"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Supprimer mon compte
                                </button>
                            </Section>
                        </Card>
                    )}

                    {activeTab === "api" && <ApiKeysCard />}

                    {activeTab === "admin" && (
                        <Card>
                            <Section title="Back-office" description="Paramètres réservés aux administrateurs.">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-ink">Afficher le menu Back-office</p>
                                        <p className="mt-1 text-xs text-ink-muted">L'élément Back-office apparaît en rouge dans la barre latérale.</p>
                                    </div>
                                    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                                        <input type="checkbox" checked={showAdminRed} onChange={handleAdminRedToggle} className="peer sr-only" />
                                        <div className="peer h-6 w-11 rounded-full bg-surface-sunken after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-border after:bg-white after:transition-all after:content-[''] peer-checked:bg-ink peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/10"></div>
                                    </label>
                                </div>
                            </Section>
                        </Card>
                    )}
                </>
            )}

            <Modal open={showDeleteModal} title="Confirmer la suppression du compte" onClose={() => setShowDeleteModal(false)}>
                <p className="mb-6 text-sm text-ink">Cela supprimera définitivement votre compte et toutes les données associées. Êtes-vous sûr ?</p>
                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="rounded-lg bg-danger px-4 py-2 font-medium text-white transition-colors duration-150 hover:bg-danger/90 disabled:opacity-60"
                    >
                        {deleting ? "Suppression…" : "Supprimer le compte"}
                    </button>
                </div>
            </Modal>
        </div>
    );
}
